import { Header as BitHeader } from '@bestseller/retail-planning.ui.atoms.header';
import { BrandImage } from '@bestseller-bit/frontend-community.components.brand-image';

import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { Box, IconButton, Skeleton, Stack, styled, Tooltip } from '@mui/material';
import { FC } from 'react';
import { TourProvider, useTourContext } from 'src/contexts/OnboardingTourContext';
import { Module, ScopeEnum, Spacings } from 'src/domain';
import { useChain } from 'src/hooks/chain/useChain';
import { StoreDetails } from 'src/hooks/turnover/useTurnoverDetails';
import { useCurrentModule } from 'src/hooks/useCurrentModule';
import { useForcedCurrency } from 'src/hooks/useForcedCurrency';
import { useScope } from 'src/hooks/useScope';
import { StoreModel } from 'src/infrastructure/rest-api/api-types';
import { StoreInfoContainer, TurnoverStoreInfoContainer } from '../StoreInfoContainer/StoreInfoContainer';

interface Props {
    store?: StoreModel;
    storeDetails?: StoreDetails;
    storeName?: string | null;
    country?: string | null;
    cluster?: string | null;
    partner?: string | null;
    chain?: string | null;
    moduleName?: string | null;
    productLineNumber: string | undefined;
    level?: ScopeEnum;
    loading?: boolean;
}

interface BaseComponentProp {
    title: string | undefined | null;
    productLineNumber: string | undefined;
    hideInfo?: boolean;
    store?: StoreModel;
    partner?: string | null;
    currency?: string | null;
}

const BaseComponent: FC<BaseComponentProp> = ({ title, productLineNumber, hideInfo, store, partner, currency }) => {
    const { startTour, steps } = useTourContext();

    const hasTour = steps && steps.length > 0;
    return (
        <Stack
            className="title-card"
            sx={{
                gap: Spacings.xSmall,
            }}
        >
            <BitHeader
                title={
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        {title ?? ''}
                        {hasTour && (
                            <Tooltip title="Help tour">
                                <IconButton
                                    aria-label="Help tour"
                                    data-dd-action-name="Open help tour"
                                    size="small"
                                    onClick={startTour}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                }
                postfix={
                    <Box
                        sx={{
                            maxWidth: '250px',
                            display: 'flex',
                        }}
                    >
                        {productLineNumber && <BrandImage productLineNumber={productLineNumber} />}
                    </Box>
                }
            >
                {!hideInfo && <StoreInfoContainer store={store} partner={partner} currency={currency} />}
            </BitHeader>
        </Stack>
    );
};

interface TurnoverStoreTitleCardProps {
    title: string | undefined | null;
    productLineNumber: string | undefined;
    chain: string | undefined;
    storeDetails?: StoreDetails | undefined;
    currency: string | undefined | null;
}

const TurnoverStoreTitleCard: FC<TurnoverStoreTitleCardProps> = ({
    title,
    productLineNumber,
    chain,
    storeDetails,
    currency,
}) => {
    return (
        <BitHeader
            title={title ?? ''}
            postfix={
                <Box
                    sx={{
                        maxWidth: '250px',
                        display: 'flex',
                    }}
                >
                    {productLineNumber && <BrandImage productLineNumber={productLineNumber} />}
                </Box>
            }
        >
            {storeDetails && (
                <TurnoverStoreInfoContainer chain={chain} currency={currency} storeDetails={storeDetails} />
            )}
        </BitHeader>
    );
};

const TitleCard: FC<Props> = ({
    level,
    productLineNumber,
    store,
    storeDetails,
    storeName,
    country,
    cluster,
    moduleName,
    partner,
    loading,
}) => {
    const chain = useChain();
    const currentLevel = useScope();
    const levelToUse = level ?? currentLevel;
    const module = useCurrentModule();
    const currency = useForcedCurrency();

    const clusterText = cluster && 'Cluster ' + cluster;

    const ClusterView = <BaseComponent productLineNumber={productLineNumber} title={clusterText} />;
    const PartnerView = <BaseComponent productLineNumber={productLineNumber} title={country} partner={partner} />;
    const StoreView = (
        <BaseComponent
            productLineNumber={productLineNumber}
            title={storeName}
            hideInfo={module === Module.Turnover}
            store={store}
            currency={currency}
        />
    );
    const TurnoverStoreView = (
        <TurnoverStoreTitleCard
            title={storeName}
            productLineNumber={productLineNumber}
            chain={chain}
            currency={currency}
            storeDetails={storeDetails}
        />
    );
    const ModuleView = <BaseComponent productLineNumber={productLineNumber} title={moduleName} />;

    return (
        <TourProvider currentModule={module}>
            {loading && (
                <TitleCardContainer>
                    <Skeleton width={200} height={40} />
                    <Skeleton width={260} height={20} />
                </TitleCardContainer>
            )}
            {!loading && (
                <Wrapper>
                    <TitleCardContainer>
                        {moduleName ? (
                            ModuleView
                        ) : (
                            <>
                                {levelToUse === ScopeEnum.CLUSTER && ClusterView}
                                {levelToUse === ScopeEnum.PARTNER && PartnerView}
                                {levelToUse === ScopeEnum.STORE && module !== Module.Turnover && StoreView}
                                {levelToUse === ScopeEnum.STORE && module === Module.Turnover && TurnoverStoreView}
                            </>
                        )}
                    </TitleCardContainer>
                </Wrapper>
            )}
        </TourProvider>
    );
};

const TitleCardContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(4),
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    alignSelf: 'stretch',
    border: '1px solid',
    borderColor: theme.palette.divider,
}));

const Wrapper = styled(Box)({
    display: 'flex',
});

export default TitleCard;
